import { useRef, useState } from "react";
import { ImagePlus, Link2, Loader2, X } from "lucide-react";
import { contentApi, COVER_MAX_MB } from "../api";
import { useToast } from "@/app/providers/Toaster";

/**
 * Portada de la pieza: se sube desde el computador (queda en webp, pública) o
 * se pega una URL. La app la muestra 4:3; si no hay portada usa el color del tipo.
 */
export function CoverField({ value, onChange }: { value: string; onChange: (url: string) => void }) {
  const { push } = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [showUrl, setShowUrl] = useState(false);

  const pick = async (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      push({ kind: "error", message: "La portada tiene que ser una imagen (JPG, PNG o WebP)." });
      return;
    }
    if (file.size > COVER_MAX_MB * 1024 * 1024) {
      push({ kind: "error", message: `La imagen pesa más de ${COVER_MAX_MB} MB.` });
      return;
    }
    setUploading(true);
    try {
      const url = await contentApi.uploadCover(file);
      onChange(url);
      push({ kind: "success", message: "Portada subida." });
    } catch (e) {
      push({ kind: "error", message: e instanceof Error ? e.message : "No se pudo subir la imagen." });
    } finally {
      setUploading(false);
      if (input.current) input.current.value = "";
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs font-medium text-fg-muted">Portada</span>
      <div className="flex items-start gap-3">
        <div className="relative w-36 shrink-0 overflow-hidden rounded-md border border-border-subtle bg-bg-subtle" style={{ aspectRatio: "4 / 3" }}>
          {value ? (
            <img src={value} alt="Portada" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-2xs text-fg-subtle">Sin portada</div>
          )}
          {uploading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
              <Loader2 size={18} className="animate-spin text-white" />
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-2">
            <button type="button" className="nx-btn-secondary" onClick={() => input.current?.click()} disabled={uploading}>
              <ImagePlus size={14} /> {value ? "Cambiar imagen" : "Subir imagen"}
            </button>
            <button type="button" className="nx-btn-secondary" onClick={() => setShowUrl((v) => !v)}>
              <Link2 size={14} /> Pegar URL
            </button>
            {value && (
              <button type="button" className="nx-btn-secondary" onClick={() => onChange("")}>
                <X size={14} /> Quitar
              </button>
            )}
          </div>
          <p className="text-2xs text-fg-subtle">
            Horizontal, idealmente 1600 × 1200 (4:3). Hasta {COVER_MAX_MB} MB; se guarda en WebP.
          </p>
        </div>
      </div>
      {showUrl && (
        <input
          className="nx-input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://…"
        />
      )}
      <input
        ref={input}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => pick(e.target.files?.[0])}
      />
    </div>
  );
}
